import { useEffect, useRef, useState } from "react";

export type EditableLabelProps = {
  readonly value: string;
  readonly isEditing: boolean;
  readonly onStartEdit: () => void;
  readonly onCommit: (value: string) => void;
  readonly onCancel: () => void;
};

export function EditableLabel({
  value,
  isEditing,
  onStartEdit,
  onCommit,
  onCancel,
}: EditableLabelProps) {
  const [editValue, setEditValue] = useState(value);

  const inputRef = useRef<HTMLInputElement>(null);

  // enter/escape already resolved the edit; the following blur must not commit again
  const blurHandledRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      setEditValue(value);
    }
  }, [isEditing, value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        type="text"
        aria-label={value}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={(e) => {
          // keep tree keyboard navigation away from the input
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            blurHandledRef.current = true;
            onCommit(editValue);
          } else if (e.key === "Escape") {
            e.preventDefault();
            blurHandledRef.current = true;
            onCancel();
          }
        }}
        onBlur={() => {
          if (blurHandledRef.current) {
            blurHandledRef.current = false;
            return;
          }
          onCommit(editValue);
        }}
        onClick={(e) => e.stopPropagation()}
        className={`text-text-primary caret-primary min-w-0 flex-1 bg-transparent text-sm outline-none`}
        ref={inputRef}
      />
    );
  }

  return (
    <button
      type="button"
      className="text-text-primary min-w-0 truncate text-left text-sm"
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onStartEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "F2") {
          e.preventDefault();
          e.stopPropagation();
          onStartEdit();
        }
      }}
    >
      {value}
    </button>
  );
}
